import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ImageUploader } from "./ImageUploader";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plane, Heart, Coffee, Mountain, Home, Camera, Trees, Star, Compass, Map, Sparkles, BookOpen, Flame, Anchor } from "lucide-react";

const ICONS = [
  { value: "Plane", Icon: Plane },
  { value: "Heart", Icon: Heart },
  { value: "Coffee", Icon: Coffee },
  { value: "Mountain", Icon: Mountain },
  { value: "Home", Icon: Home },
  { value: "Camera", Icon: Camera },
  { value: "Trees", Icon: Trees },
  { value: "Star", Icon: Star },
  { value: "Compass", Icon: Compass },
  { value: "Map", Icon: Map },
  { value: "Sparkles", Icon: Sparkles },
  { value: "BookOpen", Icon: BookOpen },
  { value: "Flame", Icon: Flame },
  { value: "Anchor", Icon: Anchor },
];

const COLORS = [
  "#7C9082", // sage
  "#C85A41", // terracotta
  "#D5A062", // gold
  "#2C2A29", // ink
  "#9C7B5C", // mocha
  "#6B7B8C", // slate blue
  "#A87C9F", // muted rose
  "#88A09F", // dusty teal
];

export const CreateListDialog = ({ open, onOpenChange, onCreated, initial = null }) => {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [color, setColor] = useState(initial?.color || COLORS[0]);
  const [icon, setIcon] = useState(initial?.icon || "Heart");
  const [coverImage, setCoverImage] = useState(initial?.cover_image || null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await api.patch(`/lists/${initial.id}`, { title, description, color, icon, cover_image: coverImage });
      } else {
        res = await api.post("/lists", { title, description, color, icon, cover_image: coverImage });
      }
      toast.success(isEdit ? "Liste aktualisiert" : "Liste erstellt");
      onCreated && onCreated(res.data);
      onOpenChange(false);
      // Reset
      if (!isEdit) {
        setTitle(""); setDescription(""); setColor(COLORS[0]); setIcon("Heart"); setCoverImage(null);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-alabaster border-border max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">{isEdit ? "Liste bearbeiten" : "Neue Liste"}</DialogTitle>
          <DialogDescription className="text-text_secondary">
            Gib eurer Liste einen Namen und eine Stimmung.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Titel</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Reisen 2026"
              className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 text-lg font-serif focus-visible:ring-0 focus-visible:border-ink"
              data-testid="list-title-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Beschreibung</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Wohin wollt ihr dieses Jahr gemeinsam reisen?"
              className="bg-transparent border-x-0 border-t-0 border-b border-border rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-ink resize-none"
              rows={2}
              data-testid="list-description-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Coverbild (optional)</label>
            <div className="mt-3">
              <ImageUploader value={coverImage} onChange={setCoverImage} aspect="video" testid="list-cover-uploader" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Farbe</label>
            <div className="flex gap-2 mt-3">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-ink scale-110" : "hover:scale-110"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-text_secondary">Icon</label>
            <div className="grid grid-cols-7 gap-2 mt-3">
              {ICONS.map(({ value, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setIcon(value)}
                  className={`aspect-square rounded-xl border flex items-center justify-center transition-all ${
                    icon === value ? "bg-ink text-alabaster border-ink" : "bg-transparent text-text_secondary border-border hover:border-ink hover:text-ink"
                  }`}
                  data-testid={`icon-${value}`}
                >
                  <Icon size={16} strokeWidth={1.6} />
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Abbrechen</Button>
            <Button type="submit" disabled={loading || !title.trim()} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="list-submit-button">
              {loading ? "Speichern …" : isEdit ? "Speichern" : "Liste erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateListDialog;
